import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import useTranslation from "../hooks/useTranslation";
import { resolveThreeDSContent } from "../utils/threeDSChallengeContent";

/**
 * Classic Payment Link 3DS challenge modal.
 *
 * Supports Nicepay HTML form markup and JSON redirectUrlHttp, and Midtrans
 * redirect URLs. See `threeDSChallengeContent.js` for the sniff contract.
 *
 * HTML is written via iframe.contentDocument.write so <base>, scripts, and
 * form action (mpgsAuth.do) stay intact — blob URLs broke Mastercard ACS.
 * JSON redirectUrlHttp / plain URLs load as iframe.src.
 *
 * Completion: Nicepay postMessages `nicepay_3ds_result`. Midtrans (and
 * Nicepay JSON-3DS if postMessage is lost) need parent polling; pass
 * `onIframeLoad` for faster probes on ACS navigations.
 */
export default function ThreeDSChallengeModal({ isOpen, content, onClose, onResult, onIframeLoad }) {
  const { t } = useTranslation();
  const iframeRef = useRef(null);
  const blobUrlRef = useRef(null);
  const htmlWrittenRef = useRef(false);
  const onResultRef = useRef(onResult);
  const onIframeLoadRef = useRef(onIframeLoad);
  const [modalHeights, setModalHeights] = useState({ modal: 500, iframe: 420 });
  const [iframeSrc, setIframeSrc] = useState(null);

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    onIframeLoadRef.current = onIframeLoad;
  }, [onIframeLoad]);

  // Enable clicks on the dedicated portal root while open (root defaults to pointer-events:none).
  useEffect(() => {
    const root = document.getElementById("payment-link-modal-root");
    if (!root) return undefined;
    if (isOpen) {
      root.style.pointerEvents = "auto";
    } else {
      root.style.pointerEvents = "none";
    }
    return () => {
      root.style.pointerEvents = "none";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const updateHeights = () => {
      const modal = Math.round(window.innerHeight * 0.8);
      setModalHeights({ modal, iframe: Math.max(320, modal - 100) });
    };
    updateHeights();
    window.addEventListener("resize", updateHeights);
    return () => window.removeEventListener("resize", updateHeights);
  }, [isOpen]);

  // HTML → document.write (blob fallback). URL / JSON redirectUrlHttp → iframe.src.
  useEffect(() => {
    if (!isOpen || !content) {
      setIframeSrc(null);
      htmlWrittenRef.current = false;
      return undefined;
    }

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    const resolved = resolveThreeDSContent(content);
    if (resolved.kind === "empty") {
      setIframeSrc(null);
      htmlWrittenRef.current = false;
      return undefined;
    }

    if (resolved.kind === "url") {
      htmlWrittenRef.current = false;
      setIframeSrc(resolved.url);
      return undefined;
    }

    setIframeSrc(null);
    htmlWrittenRef.current = false;

    const run = () => {
      const iframe = iframeRef.current;
      if (!iframe || htmlWrittenRef.current) return;
      try {
        if (!iframe.contentDocument) {
          throw new Error("iframe document unavailable");
        }
        iframe.contentDocument.open();
        iframe.contentDocument.write(resolved.html);
        iframe.contentDocument.close();
        htmlWrittenRef.current = true;
        setTimeout(() => {
          try {
            const form =
              iframe.contentDocument.querySelector("form[id='mpgsAuthFrm']") ||
              iframe.contentDocument.querySelector("form");
            if (form) form.submit();
          } catch (_) {}
        }, 100);
      } catch (_) {
        const blob = new Blob([resolved.html], { type: "text/html" });
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setIframeSrc(url);
      }
    };

    const id = setTimeout(run, 50);
    return () => {
      clearTimeout(id);
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [isOpen, content]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== "nicepay_3ds_result") return;
      onResultRef.current?.(data);
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isOpen]);

  if (!isOpen) return null;

  const modal = (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/50 p-4"
      style={{ position: "fixed", inset: 0, zIndex: 2147483647, pointerEvents: "auto" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tds-modal-title"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col"
        style={{ height: `${modalHeights.modal}px` }}
      >
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between shrink-0">
          <h3 id="tds-modal-title" className="text-lg font-semibold text-gray-900">
            {t("paymentInstructionCard.threeDsTitle")}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label={t("common.close")}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="bg-gray-100 flex flex-col shrink-0" style={{ height: `${modalHeights.iframe}px` }}>
          {content ? (
            <iframe
              ref={iframeRef}
              title={t("paymentInstructionCard.threeDsIframeTitle")}
              name="payment-link-tds-iframe"
              src={iframeSrc || undefined}
              className="w-full border-0"
              style={{ height: `${modalHeights.iframe}px` }}
              onLoad={() => onIframeLoadRef.current?.()}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              {t("paymentInstructionCard.threeDsLoading")}
            </div>
          )}
        </div>
        <p className="px-4 py-2 text-sm text-gray-500 text-center border-t border-gray-100">
          {t("paymentInstructionCard.threeDsFooter")}
        </p>
      </div>
    </div>
  );

  return createPortal(modal, document.getElementById("payment-link-modal-root") || document.body);
}
